/*
-------------------------------------------------------------------------------------
Authors         : Alinsonorin, John Myl B., Awi, Joseph Kahl L., Gozon, Daniel Allan
Date Created    : April 30, 2025
Folder          : src/components/homepage/index
File            : Homepage.tsx
Description     : 
    This component allows the user to access the homepage of the web app.
Copyright © 2025. All rights reserved.
-------------------------------------------------------------------------------------
*/



// Import React Systems
import { useState } from 'react'

// Imports MUI Components
import {
  Box,
  Grid,
  Link,
  Stack,
  Typography,
  Card,
  CardMedia,
  CardContent,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails 
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"

const AccordionComp = ({
  section,
  title,
  subtitle,
  destination
}) => {

  // Initialization states
  const [expanded, setExpanded] = useState(false)

  // Function to handle accordion expansion
  const handleChange = (isExpanded, panel) => {
    setExpanded(isExpanded ? panel : false)
  }

  const accordionStylesFaqs = {
    margin: "10px auto",
    backgroundColor: "#FDF9F3",
    transition:"0.2s ease-in-out",
    "&:hover": {
        padding: "10px 10px",
        backgroundColor: "#EAE6DA",
    },
  }

  return (
    <>
              {/* FAQs Section */}
              <Box
                flexDirection={"column"}
                component="section"
                sx={{
                  color: "#FDF9F3",
                  width: "100%",
                  // backgroundColor: "#FEFEFE",
                  margin: "0 auto",
                  display: "flex",
                  justifyContent: "space-evenly",
                  alignItems: "center",
                  padding: "80px 0",
                }}
                >

                  {/* FAQs Title Wrapper */}
                  <Box
                    sx={{
                      width: "85%",
                      margin: "20px auto",
                      display: "flex",
                      justifyContent: "space-evenly",
                      flexDirection: "column",
                      // backgroundColor: "gray",
                    }}>

                    <Stack direction={"column"} spacing={1}>
                      <Typography 
                        variant="h2"
                      >FAQs</Typography>
                    </Stack>
                    
                  </Box>

                  {/* FAQs Cards Wrapper */}
                  <Box
                    sx={{
                      width: "85%",
                      margin: "20px auto",
                      display: "flex",
                      justifyContent: "space-between",
                      flexDirection: "column",
                      // backgroundColor: "gray",
                      padding: "0px 10px",
                    }}>

                    <Accordion 
                      elevation={0}
                      expanded={expanded === "faq1"}
                      onChange={(event, isExpanded) => handleChange(isExpanded, "faq1")}
                      sx={accordionStylesFaqs}>
                      <AccordionSummary
                        id="faq1-header"
                        aria-controls="faq1-content"
                        expandIcon={<ExpandMoreIcon/>}
                        >

                          <Typography 
                            sx={{ 
                              fontWeight: expanded === "faq1" ? "bold" : "normal",
                              fontSize: expanded === "faq1" ? "17px" : "16px",
                              }}>
                              What is tooth decay, and how can it be prevented? 
                          </Typography>

                      </AccordionSummary>
                      <AccordionDetails>

                        <Typography>
                            Tooth decay, also known as cavities, occurs when bacteria in the plaque on your teeth produce acids that erode the tooth's enamel. Tooth decay can be prevented through good oral hygiene practices like brushing with fluoride toothpaste, flossing, and regular dental checkups.
                        </Typography>

                      </AccordionDetails>
                    </Accordion>

                    <Accordion
                      elevation={0}
                      expanded={expanded === "faq2"}
                      onChange={(event, isExpanded) => handleChange(isExpanded, "faq2")}
                      sx={accordionStylesFaqs}>
                      <AccordionSummary
                        id="faq2-header"
                        aria-controls="faq2-content"
                        expandIcon={<ExpandMoreIcon/>}>

                          <Typography 
                            sx={{ 
                              fontWeight: expanded === "faq2" ? "bold" : "normal",
                              fontSize: expanded === "faq2" ? "17px" : "16px",
                            }}>
                              Can tooth pain go away on its own?
                          </Typography>

                      </AccordionSummary>
                      <AccordionDetails>

                        <Typography>
                            In some cases, tooth pain can resolve on its own, particularly if it's due to temporary irritations or minor sensitivity. However, if the pain persists, worsens, or is accompanied by other symptoms like swelling or fever, it's crucial to seek dental care. Ignoring toothaches can allow underlying issues to progress and potentially lead to more serious complications. 
                        </Typography>

                      </AccordionDetails>
                    </Accordion>
                    
                    <Accordion
                      elevation={0}
                      expanded={expanded === "faq3"}
                      onChange={(event, isExpanded) => handleChange(isExpanded, "faq3")}
                      sx={accordionStylesFaqs}>
                      <AccordionSummary
                        id="faq3-header"
                        aria-controls="faq3-content"
                        expandIcon={<ExpandMoreIcon/>}>

                          <Typography 
                            sx={{ 
                              fontWeight: expanded === "faq3" ? "bold" : "normal",
                              fontSize: expanded === "faq3" ? "17px" : "16px",
                            }}>
                              What is tooth implantation?
                          </Typography>

                      </AccordionSummary>
                      <AccordionDetails>

                        <Typography>
                            Tooth implantation, or dental implants, involve surgically placing artificial tooth roots (implant) into the jawbone to replace missing teeth. These implants, often made of titanium, fuse with the bone and provide a base for a replacement tooth (crown) or a bridge.
                        </Typography>

                      </AccordionDetails>
                    </Accordion>

                    <Accordion
                      elevation={0}
                      expanded={expanded === "faq4"}
                      onChange={(event, isExpanded) => handleChange(isExpanded, "faq4")}
                      sx={accordionStylesFaqs}>
                      <AccordionSummary
                        id="faq4-header"
                        aria-controls="faq4-content"
                        expandIcon={<ExpandMoreIcon/>}>

                          <Typography 
                            sx={{ 
                              fontWeight: expanded === "faq4" ? "bold" : "normal",
                              fontSize: expanded === "faq4" ? "17px" : "16px",
                            }}>
                              What pain relief methods are used during dental procedures?
                          </Typography>

                      </AccordionSummary>
                      <AccordionDetails>

                        <Typography>
                            During dental procedures, a variety of pain relief methods are used, ranging from topical and local anesthetics to different levels of sedation. Topical anesthetics like benzocaine numb the surface of the mouth before injections. Local anesthetics, such as lidocaine or novocaine, are injected to numb a specific area, like during fillings or extractions.
                        </Typography>

                      </AccordionDetails>
                    </Accordion>

                  </Box>

              </Box>
        
    </>
  )

}

export default AccordionComp;